import React, { useEffect, useState } from 'react';
import { zoom as zoomApi, clients as clientsApi, projects as projectsApi } from '@/utils/api';
import { Video, Plus, Trash2, X, ExternalLink, Clock, Copy, Mail } from 'lucide-react';
import { toast } from 'sonner';

export default function ZoomMeetings({ scopedProjectId = null, isOperator = true }) {
  const [list, setList] = useState([]);
  const [clientsList, setClientsList] = useState([]);
  const [projectsList, setProjectsList] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    topic: '',
    start_time: '',
    duration: 30,
    client_id: '',
    project_id: scopedProjectId || '',
    invitee_email: '',
    agenda: '',
  });
  const [busy, setBusy] = useState(false);

  const reload = () => zoomApi.listMeetings(scopedProjectId ? { project_id: scopedProjectId } : {}).then(r => setList(r.data));
  useEffect(() => {
    reload();
    if (isOperator) {
      clientsApi.list().then(r => setClientsList(r.data));
      projectsApi.list().then(r => setProjectsList(r.data));
    }
  }, [scopedProjectId]); // eslint-disable-line

  // When client changes, prefill invitee email
  useEffect(() => {
    if (form.client_id) {
      const c = clientsList.find(c => c.id === form.client_id);
      if (c?.email && !form.invitee_email) setForm(f => ({ ...f, invitee_email: c.email }));
    }
  }, [form.client_id, clientsList]); // eslint-disable-line

  const submit = async () => {
    setBusy(true);
    try {
      // Convert local datetime to ISO UTC
      const iso = form.start_time ? new Date(form.start_time).toISOString() : '';
      await zoomApi.createMeeting({ ...form, start_time: iso });
      toast.success('Meeting scheduled');
      setCreating(false);
      setForm({ topic: '', start_time: '', duration: 30, client_id: '', project_id: scopedProjectId || '', invitee_email: '', agenda: '' });
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const cancel = async (id) => {
    if (!window.confirm('Cancel this meeting?')) return;
    try { await zoomApi.cancelMeeting(id); toast.success('Cancelled'); reload(); }
    catch (e) { toast.error('Failed'); }
  };

  return (
    <div className={scopedProjectId ? 'p-4' : 'p-6'} data-testid="zoom-meetings">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Video size={16} style={{ color: '#006080' }} />
          <h2 className="font-black text-base" style={{ color: 'var(--ink)' }}>
            {scopedProjectId ? 'Zoom calls' : 'Zoom meetings'}
          </h2>
        </div>
        {isOperator && (
          <button onClick={() => setCreating(true)} className="btn-primary text-xs flex items-center gap-1.5 px-3 py-1.5" data-testid="schedule-meeting-btn">
            <Plus size={11} /> Schedule
          </button>
        )}
      </div>

      {list.length === 0 ? (
        <div className="card-base p-8 text-center">
          <Video size={28} style={{ color: 'var(--border)', margin: '0 auto 10px' }} />
          <p className="text-sm" style={{ color: 'var(--muted)' }}>No meetings yet</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="meetings-list">
          {list.map(m => {
            const future = new Date(m.start_time) > new Date();
            const cancelled = m.status === 'cancelled';
            return (
              <div key={m.id} className="card-base p-3" data-testid={`meeting-${m.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-bold text-sm truncate" style={{ color: 'var(--ink)' }}>{m.topic}</p>
                      {cancelled ? (
                        <span className="badge-status" style={{ background: '#8A99A015', color: '#5C6B72' }}>Cancelled</span>
                      ) : future ? (
                        <span className="badge-status" style={{ background: '#3C8A5A15', color: '#3C8A5A' }}>Upcoming</span>
                      ) : (
                        <span className="badge-status" style={{ background: '#D9A44115', color: '#D9A441' }}>Past</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-3 mt-1 text-xs" style={{ color: 'var(--muted)' }}>
                      <span className="flex items-center gap-1"><Clock size={11} /> {new Date(m.start_time).toLocaleString()} · {m.duration}m</span>
                      {m.invitee_email && <span className="flex items-center gap-1"><Mail size={11} /> {m.invitee_email}</span>}
                    </div>
                    {m.agenda && <p className="text-xs mt-1 line-clamp-2" style={{ color: 'var(--muted)' }}>{m.agenda}</p>}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!cancelled && (
                      <>
                        <a href={m.join_url} target="_blank" rel="noreferrer" className="text-xs px-3 py-1.5 rounded-btn font-semibold flex items-center gap-1" style={{ background: '#006080', color: 'white' }} data-testid={`join-${m.id}`}>
                          <ExternalLink size={11} /> Join
                        </a>
                        <button onClick={() => { navigator.clipboard.writeText(m.join_url); toast.success('Link copied'); }} className="text-xs p-1.5 rounded-btn" style={{ color: 'var(--muted)' }} data-testid={`copy-${m.id}`}><Copy size={12} /></button>
                        {isOperator && (
                          <button onClick={() => cancel(m.id)} className="text-xs p-1.5 rounded-btn" style={{ color: '#B34A3A' }}><Trash2 size={12} /></button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {creating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="w-full max-w-lg rounded-card p-5" style={{ background: 'var(--surface)' }}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-black text-lg" style={{ color: 'var(--ink)' }}>Schedule a Zoom call</h3>
              <button onClick={() => setCreating(false)}><X size={18} style={{ color: 'var(--muted)' }} /></button>
            </div>
            <div className="space-y-2">
              <input className="input-agency text-sm" placeholder="Topic (e.g., Kick-off call)" value={form.topic} onChange={e => setForm({...form, topic: e.target.value})} data-testid="meeting-topic" />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-bold uppercase mb-1" style={{ color: 'var(--muted)' }}>Start</label>
                  <input className="input-agency text-sm" type="datetime-local" value={form.start_time} onChange={e => setForm({...form, start_time: e.target.value})} data-testid="meeting-start" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase mb-1" style={{ color: 'var(--muted)' }}>Duration (min)</label>
                  <input className="input-agency text-sm" type="number" min="5" step="5" value={form.duration} onChange={e => setForm({...form, duration: parseInt(e.target.value || '30')})} data-testid="meeting-duration" />
                </div>
              </div>
              {!scopedProjectId && (
                <select className="input-agency text-sm" value={form.client_id} onChange={e => setForm({...form, client_id: e.target.value})} data-testid="meeting-client">
                  <option value="">— Client (pulls invitee email) —</option>
                  {clientsList.map(c => <option key={c.id} value={c.id}>{c.company_name} · {c.email}</option>)}
                </select>
              )}
              <input className="input-agency text-sm" type="email" placeholder="Invitee email (auto from client)" value={form.invitee_email} onChange={e => setForm({...form, invitee_email: e.target.value})} data-testid="meeting-invitee" />
              <textarea className="input-agency text-sm resize-none" rows={3} placeholder="Agenda (optional)" value={form.agenda} onChange={e => setForm({...form, agenda: e.target.value})} />
              <div className="flex gap-2 pt-2">
                <button onClick={() => setCreating(false)} className="flex-1 py-2 text-sm rounded-btn border" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }}>Cancel</button>
                <button onClick={submit} disabled={!form.topic || !form.start_time || busy} className="flex-1 btn-primary py-2 text-sm flex items-center justify-center gap-1.5" data-testid="meeting-submit">
                  <Video size={13} /> {busy ? 'Scheduling…' : 'Schedule'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
